import {
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { LoadingButton } from "src/grading/components";
import { inputError } from "src/grading/components/InputError/InputError";
import RFModal from "src/grading/components/RFModal/RFModal";
import {
  Role,
  useInviteMemberMutation,
  useLazyGetRolesQuery,
} from "src/store/reducers/organizations/organizationsApiSlice";
import theme from "src/theme";
import * as Yup from "yup";
import useStyles from "./OrgAddUserModal.styles";

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  role: Yup.string().required("Role is required"),
});

interface OrgAddUserModalProps {
  orgId: string;
  open: boolean;
  onClose: () => void;
}

export interface OrgAddUserFormValues {
  email: string;
  role: string;
}

const OrgAddUserModal = (props: OrgAddUserModalProps) => {
  const classes = useStyles();

  const [getRoles] = useLazyGetRolesQuery();

  const [inviteMember, { isLoading }] = useInviteMemberMutation();

  const [roles, setRoles] = useState<Role[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const { roles } = await getRoles().unwrap();
      setRoles(roles);
    };

    fetchData();
  }, [getRoles]);

  const formik = useFormik({
    initialValues: {
      email: "",
      role: "",
    },
    validationSchema,
    onSubmit: async (values: OrgAddUserFormValues) => {
      const result = await inviteMember({
        organizationId: props.orgId,
        payload: values,
      }).unwrap();
      if (result.success) {
        formik.resetForm();
        props.onClose();
      } else {
        alert(result.message);
      }
    },
  });

  const { values, handleSubmit, setFieldValue, handleChange } = formik;

  return (
    <RFModal {...props} boxProps={{ className: classes.container }}>
      <Grid
        container
        component={"form"}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
      >
        <Typography variant="h5">Add User</Typography>

        <Grid container item marginTop={theme.spacing(4)} spacing={2}>
          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>Email</InputLabel>
            <TextField
              InputProps={{
                className: classes.input,
              }}
              variant="outlined"
              fullWidth
              value={values.email}
              onChange={handleChange("email")}
              error={inputError("email", formik.touched, formik.errors).error}
              helperText={
                inputError("email", formik.touched, formik.errors).display
              }
            />
          </Grid>

          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>Role</InputLabel>
            <Select
              className={classes.input}
              variant="outlined"
              fullWidth
              value={values.role}
              onChange={(e) => setFieldValue("role", e.target.value)}
            >
              {[...roles]
                .sort((a, b) => b.level - a.level)
                .map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.description}
                  </MenuItem>
                ))}
            </Select>
          </Grid>

          <Grid
            container
            item
            xs={12}
            justifyContent={"space-between"}
            marginTop={theme.spacing(4)}
          >
            <Grid item>
              <Button
                color="secondary"
                className={` ${classes.button} ${classes.backButton}`}
                onClick={props.onClose}
              >
                Cancel
              </Button>
            </Grid>

            <Grid item>
              <LoadingButton
                variant="contained"
                color="primary"
                className={classes.button}
                loading={isLoading}
                label="Save"
                type="submit"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default OrgAddUserModal;
